import { useState, useEffect, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import ReviewBox, { VerifiedBadge } from './ReviewBox';
import PiPrice from './PiPrice';
import { t } from '../i18n';

const API_BASE = '/api/petcare';

function getAuthToken() {
  return localStorage.getItem('token') || '';
}

function authHeaders() {
  const token = getAuthToken();
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
}

const colors = {
  bg: '#F7F5F0', surface: '#fff', border: '#E3E1D9', text: '#1F1E1B',
  textMuted: '#5F5E5A', primary: '#0F6E56', primaryBg: '#E1F5EE', danger: '#A32D2D',
};

const SPECIES_EMOJI = { dog: '🐕', cat: '🐈', bird: '🐦', rabbit: '🐰', other: '🐾' };

function PetCareHelpModal({ onClose }) {
  const sectionStyle = { marginBottom: 20 };
  const headingStyle = { fontSize: 16, fontWeight: 600, margin: '0 0 8px' };
  const textStyle = { fontSize: 14, color: '#3A3935', lineHeight: 1.6, margin: '0 0 8px' };
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{t('pth_title')}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>What is this?</p>
          <p style={textStyle}>{t('pth_what')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('pth_tag_q')}</p>
          <p style={textStyle}>{t('pth_tag')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('pth_health_q')}</p>
          <p style={textStyle}>{t('pth_health')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('pth_vet_q')}</p>
          <p style={textStyle}>{t('pth_vet')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('pth_mkt_q')}</p>
          <p style={textStyle}>{t('pth_mkt')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('pet_lost_found')}</p>
          <p style={textStyle}>{t('pth_lf')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('pth_prem_q')}</p>
          <p style={textStyle}>{t('pth_prem')}</p>
        </div>
        <button onClick={onClose} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('got_it')}</button>
      </div>
    </div>
  );
}

function AddPetForm({ onCreated }) {
  const [form, setForm] = useState({ name: '', species: 'dog', breed: '', gender: 'male', date_of_birth: '', bio: '', public_contact_phone: '' });
  const [photoFile, setPhotoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasBlueTick, setHasBlueTick] = useState(false);
  const [autoPost, setAutoPost] = useState(false);
  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 13, color: colors.textMuted, display: 'block', marginBottom: 4 };
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  useEffect(() => {
    fetch('/api/blue-tick/', { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setHasBlueTick(!!d.has_blue_tick))
      .catch(() => {});
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/pets/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ...form, auto_post: autoPost }) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not add pet.');
      if (photoFile) {
        const fd = new FormData();
        fd.append('photo', photoFile);
        const token = getAuthToken();
        await fetch(`${API_BASE}/pets/${data.id}/upload-photo/`, { method: 'POST', headers: token ? { Authorization: `Token ${token}` } : {}, body: fd });
      }
      alert(`Added! Tag code: ${data.tag_code}`);
      setForm({ name: '', species: 'dog', breed: '', gender: 'male', date_of_birth: '', bio: '', public_contact_phone: '' });
      setPhotoFile(null);
      onCreated();
    } catch (e) { alert(networkMessage(e)); } finally { setSubmitting(false); }
  };

  return (
    <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
      <p style={{ fontWeight: 500, fontSize: 15, margin: '0 0 12px' }}>{t('add_pet')}</p>
      <div style={{ display: 'grid', gap: 10 }}>
        <div><label style={labelStyle}>{t('name_label')}</label><input style={inputStyle} value={form.name} onChange={update('name')} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('species')}</label>
            <select style={inputStyle} value={form.species} onChange={update('species')}>
              <option value="dog">{t('dog_species')}</option><option value="cat">{t('cat_species')}</option><option value="bird">{t('bird_species')}</option><option value="rabbit">{t('rabbit_species')}</option><option value="other">{t('other_species')}</option>
            </select>
          </div>
          <div><label style={labelStyle}>{t('breed')}</label><input style={inputStyle} value={form.breed} onChange={update('breed')} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('gender')}</label>
            <select style={inputStyle} value={form.gender} onChange={update('gender')}>
              <option value="male">{t('male')}</option><option value="female">{t('female')}</option>
            </select>
          </div>
          <div><label style={labelStyle}>{t('date_of_birth')}</label><input type="date" style={inputStyle} value={form.date_of_birth} onChange={update('date_of_birth')} /></div>
        </div>
        <div><label style={labelStyle}>{t('bio_label')}</label><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={form.bio} onChange={update('bio')} /></div>
        <div><label style={labelStyle}>{t('pet_phone')}</label><input style={inputStyle} value={form.public_contact_phone} onChange={update('public_contact_phone')} /></div>
        <div><label style={labelStyle}>{t('photo_label')}</label><input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files[0])} /></div>
      </div>
      {hasBlueTick && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <input type="checkbox" id="pet-autopost" checked={autoPost} onChange={(e) => setAutoPost(e.target.checked)} />
          <label htmlFor="pet-autopost" style={{ fontSize: 13, margin: 0 }}>{t('auto_post_perk')}</label>
        </div>
      )}
      <button onClick={submit} disabled={submitting || !form.name} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer', marginTop: 12 }}>
        {submitting ? t('pet_adding') : t('pet_add')}
      </button>
    </div>
  );
}

function PetDetail({ pet, onBack, onRefresh }) {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({ record_type: 'vaccination', title: '', notes: '', weight_kg: '', record_date: '', next_due_date: '' });
  const [boosting, setBoosting] = useState(false);
  const [showListForm, setShowListForm] = useState(false);
  const [listForm, setListForm] = useState({ listing_type: 'adoption', price_pi: 0, city: '', description: '' });

  const loadRecords = useCallback(async () => {
    const res = await fetch(`${API_BASE}/pets/${pet.id}/health/`, { headers: authHeaders() });
    if (res.ok) setRecords((await safeJson(res)).records);
  }, [pet.id]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const addRecord = async () => {
    if (!form.title) return;
    try {
      await fetch(`${API_BASE}/pets/${pet.id}/health/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      setForm({ record_type: 'vaccination', title: '', notes: '', weight_kg: '', record_date: '', next_due_date: '' });
      loadRecords();
    } catch (e) { alert('Could not add record.'); }
  };

  const deleteRecord = async (id) => {
    await fetch(`${API_BASE}/health/${id}/delete/`, { method: 'DELETE', headers: authHeaders() });
    loadRecords();
  };

  const toggleLost = async () => {
    await fetch(`${API_BASE}/pets/${pet.id}/update/`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ is_lost: !pet.is_lost }) });
    onRefresh();
  };

  const applyVaccineSchedule = async () => {
    try {
      const res = await fetch(`${API_BASE}/pets/${pet.id}/apply-vaccination-schedule/`, { method: 'POST', headers: authHeaders() });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not apply schedule.');
      alert(`Added ${data.created} vaccination reminders based on ${pet.species}'s standard schedule.`);
      loadRecords();
    } catch (e) { alert(networkMessage(e)); }
  };

  const submitListing = async () => {
    try {
      const res = await fetch(`${API_BASE}/pets/${pet.id}/listings/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(listForm) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not create listing.');
      alert('Listed! Check the Marketplace tab.');
      setShowListForm(false);
    } catch (e) { alert(networkMessage(e)); }
  };

  const goPremium = async () => {
    if (!window.Pi) { alert('Open inside Pi Browser to pay with Pi.'); return; }
    setBoosting(true);
    try {
      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        { amount: 2, memo: `PetCare Premium: ${pet.name}`, metadata: { pet_id: pet.id } },
        {
          onReadyForServerApproval: (paymentId) => fetch(`${API_BASE}/pets/${pet.id}/premium/approve/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }) }),
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/pets/${pet.id}/premium/complete/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid, amount: 2, days: 365 }) })
              .then(() => { alert('Premium activated!'); setBoosting(false); onRefresh(); })
              .catch(() => setBoosting(false));
          },
          onCancel: () => setBoosting(false),
          onError: () => setBoosting(false),
        }
      );
    } catch (e) { setBoosting(false); }
  };

  const inputStyle = { padding: '8px 10px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };

  return (
    <div>
      <button onClick={onBack} style={{ fontSize: 13, color: colors.primary, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 12 }}>← {t('pet_back')}</button>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <div style={{ width: 70, height: 70, borderRadius: '50%', background: '#F1EFE8', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {pet.photo ? <img src={pet.photo} alt={pet.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 30 }}>{SPECIES_EMOJI[pet.species]}</span>}
        </div>
        <div>
          <h2 style={{ fontSize: 20, margin: 0 }}>{pet.name} {pet.is_premium && '⭐'}</h2>
          <p style={{ fontSize: 12, color: colors.textMuted, margin: 0, fontFamily: 'monospace' }}>{pet.tag_code}</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={toggleLost} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 6, background: pet.is_lost ? colors.danger : '#FBEAE3', color: pet.is_lost ? '#fff' : colors.danger, cursor: 'pointer' }}>
          {pet.is_lost ? '🚨 Marked Lost - tap to clear' : 'Mark as lost'}
        </button>
        {!pet.is_premium && (
          <button
            onClick={() => {
              fetch('/api/cart/add/', {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({
                  app_name: 'petcare', item_type: 'premium', reference_id: pet.id,
                  title: pet.name, thumbnail_url: pet.photo || '', price_pi: 2, metadata: { days: 365 },
                }),
              }).then(() => alert('Added to cart! View it from the 🛒 My Cart menu.'));
            }}
            style={{ padding: '8px 16px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 6, background: '#FFF3C4', color: '#8A6D00', cursor: 'pointer' }}
          >
            🛒 Add to Cart - Premium (2 Pi/year)
          </button>
        )}
        <button onClick={applyVaccineSchedule} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
          💉 Apply standard vaccination schedule
        </button>
        <button onClick={() => setShowListForm(!showListForm)} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
          📋 List for adoption/breeding/sale
        </button>
      </div>

      {showListForm && (
        <div style={{ background: '#F1EFE8', borderRadius: 10, padding: '0.75rem', marginBottom: 16 }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <select style={inputStyle} value={listForm.listing_type} onChange={(e) => setListForm((f) => ({ ...f, listing_type: e.target.value }))}>
              <option value="adoption">{t('pet_adoption')}</option>
              <option value="breeding">{t('pet_breeding')}</option>
              <option value="sale">{t('for_sale')}</option>
            </select>
            <input style={inputStyle} type="number" step="0.01" placeholder={t('price_in_pi')} value={listForm.price_pi} onChange={(e) => setListForm((f) => ({ ...f, price_pi: e.target.value }))} />
            <input style={inputStyle} placeholder={t('city')} value={listForm.city} onChange={(e) => setListForm((f) => ({ ...f, city: e.target.value }))} />
            <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} placeholder={t('description')} value={listForm.description} onChange={(e) => setListForm((f) => ({ ...f, description: e.target.value }))} />
          </div>
          <button onClick={submitListing} style={{ width: '100%', marginTop: 8, padding: '8px', fontSize: 13, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('publish_listing')}</button>
        </div>
      )}

      <p style={{ fontWeight: 500, fontSize: 15, margin: '0 0 8px' }}>{t('health_records')}</p>
      <div style={{ background: '#F1EFE8', borderRadius: 10, padding: '0.75rem', marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <select style={inputStyle} value={form.record_type} onChange={(e) => setForm((f) => ({ ...f, record_type: e.target.value }))}>
            <option value="vaccination">{t('vaccination')}</option><option value="vet_visit">{t('vet_visit')}</option>
            <option value="medication">{t('medication')}</option><option value="deworming">{t('deworming')}</option>
            <option value="weight">{t('weight_check')}</option><option value="other">{t('other_type')}</option>
          </select>
          <input style={inputStyle} placeholder={t('title_label')} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <input type="date" style={inputStyle} value={form.record_date} onChange={(e) => setForm((f) => ({ ...f, record_date: e.target.value }))} />
          <input type="date" placeholder={t('next_due')} style={inputStyle} value={form.next_due_date} onChange={(e) => setForm((f) => ({ ...f, next_due_date: e.target.value }))} />
        </div>
        <textarea style={{ ...inputStyle, width: '100%', resize: 'vertical', marginBottom: 8 }} rows={2} placeholder={t('notes_label')} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
        <button onClick={addRecord} style={{ padding: '8px 16px', fontSize: 13, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('add_record')}</button>
      </div>

      {records.map((r) => (
        <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: 6 }}>
          <div>
            <p style={{ fontSize: 13, fontWeight: 500, margin: 0, textTransform: 'capitalize' }}>{r.record_type.replace('_', ' ')} - {r.title}</p>
            <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>{r.record_date} {r.next_due_date && `· next due ${r.next_due_date}`}</p>
          </div>
          <button onClick={() => deleteRecord(r.id)} style={{ fontSize: 11, color: colors.danger, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      ))}
    </div>
  );
}

function LostFoundBoard() {
  const [reports, setReports] = useState([]);
  const [city, setCity] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ report_type: 'lost', description: '', city: '', location_description: '', contact_phone: '' });

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    const res = await fetch(`${API_BASE}/lostfound/?${params.toString()}`);
    if (res.ok) setReports((await safeJson(res)).reports);
  }, [city]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const res = await fetch(`${API_BASE}/lostfound/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      if (!res.ok) { alert('Please login first.'); return; }
      alert('Report posted.');
      setShowForm(false);
      load();
    } catch (e) { alert('Could not post report.'); }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input placeholder={t('filter_by_city')} value={city} onChange={(e) => setCity(e.target.value)} style={{ flex: 1, padding: '8px 12px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 16 }} />
        <button onClick={() => setShowForm(!showForm)} style={{ padding: '0 16px', fontSize: 13, border: 'none', borderRadius: 16, background: colors.primary, color: '#fff', cursor: 'pointer' }}>+ {t('pet_report')}</button>
      </div>

      {showForm && (
        <div style={{ background: '#F1EFE8', borderRadius: 10, padding: '0.75rem', marginBottom: 12 }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <select style={inputStyle} value={form.report_type} onChange={(e) => setForm((f) => ({ ...f, report_type: e.target.value }))}>
              <option value="lost">{t('lost_pet')}</option><option value="found">{t('found_stray')}</option>
            </select>
            <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} placeholder={t('pet_desc_ph')} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            <input style={inputStyle} placeholder={t('pet_city_ph')} value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
            <input style={inputStyle} placeholder={t('location_details')} value={form.location_description} onChange={(e) => setForm((f) => ({ ...f, location_description: e.target.value }))} />
            <input style={inputStyle} placeholder={t('contact_phone')} value={form.contact_phone} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} />
          </div>
          <button onClick={submit} style={{ width: '100%', marginTop: 8, padding: '8px', fontSize: 13, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('post_report')}</button>
        </div>
      )}

      {reports.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_active_reports')}</p> :
        reports.map((r) => (
          <div key={r.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8 }}>
            <span style={{ fontSize: 11, background: r.report_type === 'lost' ? '#FBEAE3' : colors.primaryBg, color: r.report_type === 'lost' ? colors.danger : colors.primary, padding: '2px 8px', borderRadius: 8, textTransform: 'uppercase', fontWeight: 600 }}>{r.report_type}</span>
            <p style={{ fontSize: 13, margin: '6px 0 2px' }}>{r.description}</p>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>📍 {r.city} {r.location_description && `- ${r.location_description}`} {r.contact_phone && `· 📞 ${r.contact_phone}`}</p>
          </div>
        ))
      }
    </div>
  );
}

function RemindersPanel() {
  const [reminders, setReminders] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/reminders/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => setReminders(d.reminders || [])).catch(() => setReminders([]));
  }, []);

  if (reminders === null) return <p style={{ color: colors.textMuted, textAlign: 'center' }}>{t('loading')}</p>;
  if (reminders.length === 0) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('pet_no_due')}</p>;

  return (
    <div>
      {reminders.map((r) => (
        <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: colors.surface, border: `0.5px solid ${r.is_overdue ? colors.danger : colors.border}`, borderRadius: 10, padding: '0.6rem 0.75rem', marginBottom: 8 }}>
          <div>
            <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>{r.pet_name} - {r.title}</p>
            <p style={{ fontSize: 11, color: colors.textMuted, margin: 0, textTransform: 'capitalize' }}>{r.record_type.replace('_', ' ')}</p>
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: r.is_overdue ? colors.danger : colors.primary }}>
            {r.is_overdue ? 'Overdue!' : new Date(r.next_due_date).toLocaleDateString()}
          </span>
        </div>
      ))}
    </div>
  );
}

function VetsPanel() {
  const [vets, setVets] = useState([]);
  const [city, setCity] = useState('');
  const [myPets, setMyPets] = useState([]);
  const [showRegister, setShowRegister] = useState(false);
  const [regForm, setRegForm] = useState({ clinic_name: '', city: '', address: '', phone: '', bio: '', consultation_fee_pi: 0 });
  const [appointments, setAppointments] = useState({ as_owner: [], as_vet: [] });
  const [bookingVetId, setBookingVetId] = useState(null);
  const [bookForm, setBookForm] = useState({ pet_id: '', requested_datetime: '', reason: '' });

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };

  const loadVets = useCallback(async () => {
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    const res = await fetch(`${API_BASE}/vets/?${params.toString()}`);
    if (res.ok) setVets((await safeJson(res)).vets);
  }, [city]);

  const loadAppointments = useCallback(async () => {
    const res = await fetch(`${API_BASE}/appointments/mine/`, { headers: authHeaders() });
    if (res.ok) setAppointments(await safeJson(res));
  }, []);

  useEffect(() => { loadVets(); }, [loadVets]);
  useEffect(() => { loadAppointments(); }, [loadAppointments]);
  useEffect(() => {
    fetch(`${API_BASE}/pets/mine/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => setMyPets(d.pets || [])).catch(() => {});
  }, []);

  const registerVet = async () => {
    try {
      await fetch(`${API_BASE}/vets/register/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(regForm) });
      alert('You are now listed as a vet provider!');
      setShowRegister(false);
      loadVets();
    } catch (e) { alert('Could not register.'); }
  };

  const bookAppointment = async () => {
    try {
      const res = await fetch(`${API_BASE}/vets/${bookingVetId}/book/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(bookForm) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not book.');
      alert('Appointment requested!');
      setBookingVetId(null);
      loadAppointments();
    } catch (e) { alert(networkMessage(e)); }
  };

  const respondAppointment = async (id, decision) => {
    await fetch(`${API_BASE}/appointments/${id}/respond/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ decision }) });
    loadAppointments();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input placeholder={t('filter_by_city')} value={city} onChange={(e) => setCity(e.target.value)} style={{ flex: 1, padding: '8px 12px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 16 }} />
        <button onClick={() => setShowRegister(!showRegister)} style={{ padding: '0 16px', fontSize: 13, border: 'none', borderRadius: 16, background: colors.primary, color: '#fff', cursor: 'pointer' }}>+ {t('pet_reg_vet')}</button>
      </div>

      {showRegister && (
        <div style={{ background: '#F1EFE8', borderRadius: 10, padding: '0.75rem', marginBottom: 12 }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <input style={inputStyle} placeholder={t('clinic_name')} value={regForm.clinic_name} onChange={(e) => setRegForm((f) => ({ ...f, clinic_name: e.target.value }))} />
            <input style={inputStyle} placeholder={t('city')} value={regForm.city} onChange={(e) => setRegForm((f) => ({ ...f, city: e.target.value }))} />
            <input style={inputStyle} placeholder={t('address')} value={regForm.address} onChange={(e) => setRegForm((f) => ({ ...f, address: e.target.value }))} />
            <input style={inputStyle} placeholder={t('phone_label')} value={regForm.phone} onChange={(e) => setRegForm((f) => ({ ...f, phone: e.target.value }))} />
            <input style={inputStyle} type="number" step="0.01" placeholder={t('consultation_fee')} value={regForm.consultation_fee_pi} onChange={(e) => setRegForm((f) => ({ ...f, consultation_fee_pi: e.target.value }))} />
            <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} placeholder={t('bio_label')} value={regForm.bio} onChange={(e) => setRegForm((f) => ({ ...f, bio: e.target.value }))} />
          </div>
          <button onClick={registerVet} style={{ width: '100%', marginTop: 8, padding: '8px', fontSize: 13, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('save_vet_profile')}</button>
        </div>
      )}

      {(appointments.as_owner.length > 0 || appointments.as_vet.length > 0) && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('my_appointments')}</p>
          {appointments.as_owner.map((a) => (
            <div key={`o${a.id}`} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 8, padding: '0.5rem 0.75rem', marginBottom: 6, fontSize: 12 }}>
              {a.pet_name} @ {a.clinic_name} - {new Date(a.requested_datetime).toLocaleString()} <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>({a.status})</span>
            </div>
          ))}
          {appointments.as_vet.map((a) => (
            <div key={`v${a.id}`} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 8, padding: '0.5rem 0.75rem', marginBottom: 6, fontSize: 12 }}>
              {a.pet_name} (@{a.owner_username}) - {new Date(a.requested_datetime).toLocaleString()} <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>({a.status})</span>
              {a.status === 'requested' && (
                <div style={{ marginTop: 4 }}>
                  <button onClick={() => respondAppointment(a.id, 'confirmed')} style={{ fontSize: 11, padding: '3px 10px', border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer', marginRight: 6 }}>{t('confirm')}</button>
                  <button onClick={() => respondAppointment(a.id, 'cancelled')} style={{ fontSize: 11, padding: '3px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>{t('decline')}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {vets.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_vets_listed')}</p> :
        vets.map((v) => (
          <div key={v.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>{v.clinic_name}</p>
              <VerifiedBadge module="vet" objectId={v.id} />
            </div>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: '2px 0' }}>{v.city} · <PiPrice pi={v.consultation_fee_pi} /> consultation</p>
            {v.bio && <p style={{ fontSize: 12, margin: '4px 0' }}>{v.bio}</p>}
            {bookingVetId === v.id ? (
              <div style={{ marginTop: 8 }}>
                <select style={inputStyle} value={bookForm.pet_id} onChange={(e) => setBookForm((f) => ({ ...f, pet_id: e.target.value }))}>
                  <option value="">{t('select_your_pet')}</option>
                  {myPets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input type="datetime-local" style={{ ...inputStyle, marginTop: 6 }} value={bookForm.requested_datetime} onChange={(e) => setBookForm((f) => ({ ...f, requested_datetime: e.target.value }))} />
                <input style={{ ...inputStyle, marginTop: 6 }} placeholder={t('reason_for_visit')} value={bookForm.reason} onChange={(e) => setBookForm((f) => ({ ...f, reason: e.target.value }))} />
                <button onClick={bookAppointment} style={{ width: '100%', marginTop: 6, padding: '8px', fontSize: 13, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('request_appointment')}</button>
              </div>
            ) : (
              <button onClick={() => setBookingVetId(v.id)} style={{ marginTop: 6, fontSize: 12, padding: '6px 14px', border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>{t('book_appointment')}</button>
            )}
            <ReviewBox
              module="vet"
              objectId={v.id}
              objectTitle={v.clinic_name}
              isAdmin={localStorage.getItem('username') === 'khalidxpert'}
            />
          </div>
        ))
      }
    </div>
  );
}

function MarketplacePanel() {
  const [listings, setListings] = useState([]);
  const [type, setType] = useState('');
  const [inquireId, setInquireId] = useState(null);
  const [inquireForm, setInquireForm] = useState({ message: '', contact_phone: '' });

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    const res = await fetch(`${API_BASE}/listings/?${params.toString()}`);
    if (res.ok) setListings((await safeJson(res)).listings);
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const sendInquiry = async (id) => {
    try {
      await fetch(`${API_BASE}/listings/${id}/inquire/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(inquireForm) });
      alert('Inquiry sent to the owner!');
      setInquireId(null);
      setInquireForm({ message: '', contact_phone: '' });
    } catch (e) { alert('Could not send inquiry.'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {['', 'adoption', 'breeding', 'sale'].map((t) => (
          <button key={t} onClick={() => setType(t)} style={{ padding: '5px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, cursor: 'pointer', background: type === t ? colors.primary : '#fff', color: type === t ? '#fff' : colors.textMuted, textTransform: 'capitalize' }}>
            {t === '' ? 'All' : t}
          </button>
        ))}
      </div>

      {listings.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_listings_now')}</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
          {listings.map((l) => (
            <div key={l.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ width: '100%', aspectRatio: '1', background: '#F1EFE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {l.pet_photo ? <img src={l.pet_photo} alt={l.pet_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 28 }}>{SPECIES_EMOJI[l.pet_species]}</span>}
              </div>
              <div style={{ padding: '0.6rem' }}>
                <span style={{ fontSize: 10, background: colors.primaryBg, color: colors.primary, padding: '2px 8px', borderRadius: 8, textTransform: 'capitalize', fontWeight: 600 }}>{l.listing_type}</span>
                <p style={{ fontSize: 13, fontWeight: 500, margin: '6px 0 2px' }}>{l.pet_name}</p>
                <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 4px' }}>{l.pet_breed} · {l.city}</p>
                <p style={{ fontSize: 13, fontWeight: 600, color: colors.primary, margin: '0 0 6px' }}>{l.price_pi > 0 ? <PiPrice pi={l.price_pi} /> : 'Free'}</p>
                {inquireId === l.id ? (
                  <div>
                    <textarea style={{ ...inputStyle, resize: 'vertical', marginBottom: 6 }} rows={2} placeholder={t('message_label')} value={inquireForm.message} onChange={(e) => setInquireForm((f) => ({ ...f, message: e.target.value }))} />
                    <input style={{ ...inputStyle, marginBottom: 6 }} placeholder={t('your_phone')} value={inquireForm.contact_phone} onChange={(e) => setInquireForm((f) => ({ ...f, contact_phone: e.target.value }))} />
                    <button onClick={() => sendInquiry(l.id)} style={{ width: '100%', padding: '6px', fontSize: 12, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('send')}</button>
                  </div>
                ) : (
                  <button onClick={() => setInquireId(l.id)} style={{ width: '100%', padding: '6px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>{t('inquire')}</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TagLookupPanel() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState('');

  const lookup = async () => {
    setMsg(''); setResult(null);
    try {
      const res = await fetch(`${API_BASE}/tag/${encodeURIComponent(code)}/`);
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Not found.');
      setResult(data);
    } catch (e) { setMsg(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input placeholder={t('enter_tag_code') + ' (e.g. PET-A1B2C3D4)'} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} style={{ flex: 1, padding: '10px 14px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 20 }} />
        <button onClick={lookup} style={{ padding: '0 20px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 20, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('look_up')}</button>
      </div>
      {msg && <p style={{ color: colors.danger, fontSize: 13 }}>{msg}</p>}
      {result && (
        <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1rem', textAlign: 'center' }}>
          {result.photo && <img src={result.photo} alt={result.name} style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 8px' }} />}
          <p style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px' }}>{SPECIES_EMOJI[result.species]} {result.name}</p>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 8px' }}>{result.breed}</p>
          {result.is_lost && (
            <div style={{ background: '#FBEAE3', color: colors.danger, padding: '10px', borderRadius: 8, marginBottom: 8 }}>
              🚨 This pet is marked lost! {result.contact_phone ? `Please contact: ${result.contact_phone}` : 'Contact info not provided.'}
            </div>
          )}
          <p style={{ fontSize: 13 }}>{result.bio}</p>
        </div>
      )}
    </div>
  );
}

export default function PetCareApp() {
  const [tab, setTab] = useState('mypets');
  const [pets, setPets] = useState([]);
  const [openPet, setOpenPet] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const loadPets = useCallback(async () => {
    const res = await fetch(`${API_BASE}/pets/mine/`, { headers: authHeaders() });
    if (res.ok) setPets((await safeJson(res)).pets);
  }, []);

  useEffect(() => { loadPets(); }, [loadPets]);

  const tabStyle = (t) => ({ padding: '8px 14px', fontSize: 13, fontWeight: 500, border: 'none', borderBottom: tab === t ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'transparent', color: tab === t ? colors.primary : colors.textMuted, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' });

  if (openPet) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: colors.bg, minHeight: '100vh' }}>
        <PetDetail pet={openPet} onBack={() => setOpenPet(null)} onRefresh={() => { loadPets(); setOpenPet(null); }} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: colors.bg, minHeight: '100vh' }}>
      {showHelp && <PetCareHelpModal onClose={() => setShowHelp(false)} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>🐾 {t('pet_app')}</h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 1rem' }}>{t('pet_tag')}</p>
        </div>
        <button onClick={() => setShowHelp(true)} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer' }}>❓ {t('pet_how')}</button>
      </div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: `0.5px solid ${colors.border}`, marginBottom: '1.25rem' }}>
        <button style={tabStyle('mypets')} onClick={() => setTab('mypets')}>{t('my_pets')}</button>
        <button style={tabStyle('reminders')} onClick={() => setTab('reminders')}>⏰ {t('pet_reminders')}</button>
        <button style={tabStyle('vets')} onClick={() => setTab('vets')}>🩺 {t('pet_vets')}</button>
        <button style={tabStyle('market')} onClick={() => setTab('market')}>🏷️ {t('pet_marketplace')}</button>
        <button style={tabStyle('lostfound')} onClick={() => setTab('lostfound')}>{t('pet_lost_found')}</button>
        <button style={tabStyle('lookup')} onClick={() => setTab('lookup')}>{t('tag_lookup')}</button>
      </div>

      {tab === 'mypets' && (
        <div>
          {pets.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10, marginBottom: '1.5rem' }}>
              {pets.map((p) => (
                <div key={p.id} onClick={() => setOpenPet(p)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: 8, cursor: 'pointer', textAlign: 'center' }}>
                  <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#F1EFE8', margin: '0 auto 6px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {p.photo ? <img src={p.photo} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 22 }}>{SPECIES_EMOJI[p.species]}</span>}
                  </div>
                  <p style={{ fontSize: 12, fontWeight: 500, margin: 0 }}>{p.name} {p.is_lost && '🚨'}</p>
                </div>
              ))}
            </div>
          )}
          <AddPetForm onCreated={loadPets} />
        </div>
      )}

      {tab === 'lostfound' && <LostFoundBoard />}
      {tab === 'reminders' && <RemindersPanel />}
      {tab === 'vets' && <VetsPanel />}
      {tab === 'market' && <MarketplacePanel />}
      {tab === 'lookup' && <TagLookupPanel />}
    </div>
  );
}
